/**
 * Adopt a network device: the SNMP peers this machine found, and can poll.
 *
 * **Offered here rather than only on the Network page because THIS machine becomes the poller.** The
 * CRM server is a NAS or a datacentre box and cannot reach a customer's printer; an agent that
 * discovered the peer is demonstrably on its network, so the device whose page this is has already
 * proved it can talk to it. Same rows, same credential form and the same endpoints as the Network
 * page's panel.
 *
 * Two exclusions are deliberate:
 *
 *   * **Only peers THIS machine discovered.** Another agent's scan may be on a different segment.
 *   * **No Synology devices.** They get a richer enrollment on the customer's Network page - private
 *     MIBs plus an optional DSM/SRM Web API login - and offering both would be a choice between a
 *     worse and a better version of the same thing. A Synology adopted here is most of the reason
 *     one used to end up monitored rather than enrolled.
 */
import { insets, type Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { DiscoveredDeviceDto, SnmpCredentials } from '../../contracts.gen.js';
import { kindLabel, looksSynology, snmpSummary } from '../../discovery.js';
import { plural } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { chip } from '../../ui/Chip.js';
import { SnmpForm } from '../../ui/SnmpForm.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, splitRow, stackOf, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTabContext } from './context.js';

/** The adopt panel, which the header's button toggles. */
export interface AdoptPanel {
  readonly panel: Card;
  /** Shows or hides the panel, loading the adoptable peers on the way in. */
  toggle(): Promise<void>;
}

export function adoptPanel(dc: DeviceTabContext): AdoptPanel {
  let open = false;
  let rows: DiscoveredDeviceDto[] = [];

  const status = new StatusLine();
  const rowHost = vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false });

  const panel = card({ visible: false });
  panel.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      h2('Adopt a network device'),
      status.label,
      rowHost
    )
  );

  /**
   * One peer: its identity, a Monitor button, and the credential form the button reveals.
   *
   * The form is built per row and owned by the row's own closure - which is the whole of what the
   * OpenSilver page needed a `FindNearest` visual-tree walk for. That walk climbed a fixed number of
   * parents to find the row's `PasswordBox`es and read the FIRST row's boxes when adopting the
   * second, silently downgrading a v3 monitor to noAuthNoPriv. Here there is nothing to find.
   */
  function buildRow(peer: DiscoveredDeviceDto): Control {
    const heading = inlineRow();
    const nameBox = textBox({
      text: peer.snmpSysName || peer.hostname || peer.ipAddress,
      width: 220,
      autoWidth: false,
      hAlign: 'start'
    });
    // The heading shows the name as it stands; the editable copy is in the form below, so renaming
    // before adopting is possible without the row's own title going stale under it.
    const headingName = value(nameBox.text, { font: FONT.bold });
    nameBox.on('textchanged', () => {
      headingName.text = nameBox.text;
    });
    heading.controls.add(headingName);
    heading.controls.add(muted(peer.ipAddress));
    heading.controls.add(chip(kindLabel(peer.kind), 'info'));

    const summary = muted(snmpSummary(peer));
    summary.visible = summary.text.length > 0;

    const left = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      heading,
      summary
    );

    const form = new SnmpForm();
    form.setCommunity(peer.snmpCommunity);
    const pollBox = textBox({ text: '60', width: 80, autoWidth: false, hAlign: 'start' });
    const rowStatus = new StatusLine();

    const testButton = button('Test connection', () => void probe());
    const adoptButton = button('Start monitoring', () => void adopt(), true);

    const formCard = card({ tint: 'ok', bordered: false, padding: insets(GAP.lg) });
    formCard.controls.add(
      stackOf(
        vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
        fieldRow(field('Name', nameBox), field('Poll every (seconds)', pollBox)),
        form.panel,
        stackOf(buttonRow(), testButton, adoptButton),
        rowStatus.label
      )
    );
    formCard.visible = false;

    const monitorButton = button('Monitor', () => {
      formCard.visible = !formCard.visible;
      dc.relayout();
    });

    const top = splitRow({ spacing: GAP.md, align: 'start' });
    fillWith(top, left);
    top.controls.add(monitorButton);

    function credentials(): SnmpCredentials | null {
      const built = form.build();
      if (!built.ok) {
        rowStatus.error(built.error);
        dc.relayout();
        return null;
      }
      // Kept through a Test - clearing them there made somebody retype both to act on the answer -
      // and emptied once the adopt succeeds.
      return built.credentials;
    }

    async function probe(): Promise<void> {
      const device = dc.device();
      if (!device) return;
      const creds = credentials();
      if (!creds) return;
      rowStatus.busy('Probing…');
      dc.relayout();
      try {
        const result = await crm.testCustomerSnmp(device.customerId, {
          ipAddress: peer.ipAddress,
          credentials: creds,
          // This machine does the probing as well as the polling: the answer is only worth having
          // from the agent that will actually be doing the work.
          pollingDeviceId: dc.deviceId
        });
        if (dc.ctx.cancelled()) return;
        rowStatus.set(result.message, result.ok ? 'ok' : 'error');
      } catch (error) {
        if (!dc.ctx.cancelled()) rowStatus.error(describeError(error));
      }
      dc.relayout();
    }

    async function adopt(): Promise<void> {
      const creds = credentials();
      if (!creds) return;
      const typed = Number.parseInt(pollBox.text.trim(), 10);
      rowStatus.busy('Adopting…');
      dc.relayout();
      try {
        const response = await crm.adoptDiscovered(peer.id, {
          displayName: nameBox.text.trim() || peer.ipAddress,
          pollSeconds: Number.isFinite(typed) && typed > 0 ? typed : 60,
          credentials: creds
        });
        if (dc.ctx.cancelled()) return;
        form.clearPasswords();
        // The adopted row leaves this list, so the outcome lands on the PANEL's status line - and it
        // is written after the reload, which sets its own status while it runs.
        const message = response.agentOnline
          ? `Adopted ${nameBox.text.trim() || peer.ipAddress} — this machine polls it now. See it ` +
            "under the customer's Monitored devices."
          : `Adopted ${nameBox.text.trim() || peer.ipAddress}, but this machine is offline — polling ` +
            'starts when it reconnects.';
        await load();
        if (dc.ctx.cancelled()) return;
        status.ok(message);
      } catch (error) {
        if (!dc.ctx.cancelled()) rowStatus.error(describeError(error));
      }
      dc.relayout();
    }

    return stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false, padding: insets(0, GAP.sm) }),
      top,
      formCard
    );
  }

  async function load(): Promise<void> {
    const device = dc.device();
    if (!device) return;
    status.busy('Looking for adoptable devices…');
    rowHost.controls.clear();
    try {
      const discovered = await crm.getDiscovered(device.customerId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      rows = discovered.filter(canAdopt);
      for (const peer of rows) rowHost.controls.add(buildRow(peer));
      status.set(
        rows.length === 0
          ? 'No adoptable SNMP devices found by this machine. Run a scan from the customer’s ' +
              'Network page, and note that Synology devices enroll there rather than here.'
          : `${plural(rows.length, 'device')} this machine can monitor. This machine does the polling.`
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  /** Only what this machine found, that answers SNMP, is not already adopted, and is not a Synology. */
  function canAdopt(peer: DiscoveredDeviceDto): boolean {
    return (
      peer.discoveredByDeviceId === dc.deviceId &&
      peer.snmpEnabled &&
      peer.adoptedMonitorId === null &&
      !looksSynology(peer)
    );
  }

  return {
    panel,
    async toggle() {
      open = !open;
      panel.visible = open;
      dc.relayout();
      if (open) await load();
    }
  };
}
